import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";

import logo from "../../assets/elitlig logo beyaz.png";
import { useUser } from "../../UserContext";
import { routeAfterLogin } from "../../hooks/useLogin";
import { isManagementRole } from "../../utils/roleManagement";
import { usePortal } from "../../portal/PortalContext";
import PortalHeader from "../../portal/PortalHeader";
import "./SiteChrome.css";
import "./SiteChromePurple.css";

/* =========================================================
   SABİTLER
========================================================= */

const navLinks = [
  {
    key: "home",
    page: "anasayfa",
    label: "Anasayfa",
    type: "scoped",
  },
  {
    key: "matches",
    page: "maclar",
    label: "Maçlar",
    type: "scoped",
  },
  {
    key: "table",
    page: "puan-durumu",
    label: "Puan Durumu",
    type: "scoped",
  },
  {
    key: "players",
    page: "oyuncular",
    label: "Oyuncular",
    type: "scoped",
  },
  {
    key: "rules",
    page: "kurallar",
    label: "Kurallar",
    type: "city",
  },
  {
    key: "penalties",
    page: "cezalar",
    label: "Cezalar",
    type: "city",
  },
  {
    key: "contact",
    page: "iletisim",
    label: "İletişim",
    type: "city",
  },
  {
    // Haberler kapsamdan bağımsızdır; şehir/lig seçilmemişken de açılır ve
    // seçim varsa haber listesi o kapsama göre süzülür.
    key: "news",
    page: "haberler",
    label: "Haberler",
    type: "global",
  },
];

/* =========================================================
   YARDIMCI FONKSİYONLAR
========================================================= */

const encodeSeg = (value) =>
  encodeURIComponent(String(value ?? "").trim());

const decodeSeg = (value) => {
  try {
    return decodeURIComponent(String(value ?? ""));
  } catch {
    return String(value ?? "");
  }
};

const getOptionId = (option) => {
  if (option === null || option === undefined) {
    return "";
  }

  if (typeof option === "object") {
    return String(
      option.id ??
        option.value ??
        option.cityId ??
        option.leagueId ??
        option.seasonId ??
        ""
    ).trim();
  }

  return String(option).trim();
};

const getOptionLabel = (option) => {
  if (option === null || option === undefined) {
    return "";
  }

  if (typeof option === "object") {
    return String(
      option.label ??
        option.name ??
        option.ad ??
        option.title ??
        option.id ??
        option.value ??
        ""
    );
  }

  return String(option);
};

const normalizeOptions = (options) => {
  if (!Array.isArray(options)) {
    return [];
  }

  return options
    .map((option) => ({
      id: getOptionId(option),
      label: getOptionLabel(option),
    }))
    .filter((option) => option.id);
};

/* =========================================================
   İKONLAR
========================================================= */

function SunIcon() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="12"
        r="4"
        stroke="currentColor"
        strokeWidth="1.8"
      />

      <path
        d="M12 2v2.2M12 19.8V22M2 12h2.2M19.8 12H22M4.9 4.9l1.6 1.6M17.5 17.5l1.6 1.6M4.9 19.1l1.6-1.6M17.5 6.5l1.6-1.6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M20.5 14.3A8.2 8.2 0 0 1 9.7 3.5 8.5 8.5 0 1 0 20.5 14.3Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M4 7h16M4 12h16M4 17h12"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MobileNavIcon({ type }) {
  const paths = {
    home: "M3 11.5 12 4l9 7.5M5.5 10v10h13V10M9.5 20v-6h5v6",
    matches: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-18 3.2 3.2-1.2 4.3-4 1.2-3-3",
    players: "M16 20v-1.5a4.5 4.5 0 0 0-4.5-4.5h-3A4.5 4.5 0 0 0 4 18.5V20m6-9a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm7-1a3 3 0 0 0 0-6m3 16v-1.5a4 4 0 0 0-3-3.9",
    table: "M5 4h14v16H5zM5 9h14M10 9v11M5 14h14",
  };

  if (type === "menu") return <MenuIcon />;

  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d={paths[type]} stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Menü ve sol ray ikonları (çizgi ikon, 24px ızgara).
const NAV_ICON_PATHS = {
  home: "M3 11.5 12 4l9 7.5M5.5 10v10h13V10M9.5 20v-6h5v6",
  matches: "M7 4h10v4a5 5 0 0 1-10 0V4ZM7 5H4v1a4 4 0 0 0 3 3.9M17 5h3v1a4 4 0 0 1-3 3.9M12 13v4m-4 4h8m-6.5-4h5",
  fixtures: "M4.5 5.5h15v14h-15zM8 3.5v4M16 3.5v4M4.5 10h15M8.5 14h2M13.5 14h2M8.5 17h2",
  table: "M5 20v-6M10 20V9M15 20v-9M20 20V5M3 20h18",
  players: "M16 20v-1.5a4.5 4.5 0 0 0-4.5-4.5h-3A4.5 4.5 0 0 0 4 18.5V20m6-9a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm7-1a3 3 0 0 0 0-6m3 16v-1.5a4 4 0 0 0-3-3.9",
  rules: "M4 5.5C6 4.5 9 4.5 12 6c3-1.5 6-1.5 8-.5v13c-2-1-5-1-8 .5-3-1.5-6-1.5-8-.5zM12 6v13.5",
  penalties: "M12 3.5 19 6v5.5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6zM12 9v4M12 16h.01",
  news: "M6 3.5h9l4 4v13H6zM15 3.5v4h4M9 11h7M9 14.5h7M9 18h4",
  contact: "M3.5 6h17v12h-17zM3.5 7l8.5 6 8.5-6",
  search: "M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14ZM20 20l-4-4",
  panel: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM19.4 13.5l1.6 1.2-2 3.5-1.9-.7a7 7 0 0 1-1.7 1l-.3 2h-4l-.3-2a7 7 0 0 1-1.7-1l-1.9.7-2-3.5 1.6-1.2a7 7 0 0 1 0-3L3 9.3l2-3.5 1.9.7a7 7 0 0 1 1.7-1l.3-2h4l.3 2a7 7 0 0 1 1.7 1l1.9-.7 2 3.5-1.6 1.2a7 7 0 0 1 0 3Z",
};

function NavIcon({ type, size = 20 }) {
  const d = NAV_ICON_PATHS[type];
  if (!d) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d={d} stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M6 6l12 12M18 6 6 18"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M12 21s7-5.1 7-11a7 7 0 1 0-14 0c0 5.9 7 11 7 11Z"
        stroke="currentColor"
        strokeWidth="1.8"
      />

      <circle
        cx="12"
        cy="10"
        r="2.6"
        stroke="currentColor"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function TrophyIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M7 4h10v4a5 5 0 0 1-10 0V4ZM7 5H4v1a4 4 0 0 0 3 3.9M17 5h3v1a4 4 0 0 1-3 3.9M12 13v4m-4 4h8m-6.5-4h5"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="3.5"
        y="5"
        width="17"
        height="16"
        rx="2.5"
        stroke="currentColor"
        strokeWidth="1.7"
      />

      <path
        d="M8 3v4M16 3v4M3.5 10h17"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* =========================================================
   KAPSAM SEÇİCİ
========================================================= */

function ScopePicker({
  cityOptions,
  leagueOptions,
  seasonOptions,
  cityId,
  leagueId,
  seasonId,
  cityReady,
  onCityChange,
  onLeagueChange,
  onSeasonChange,
}) {
  return (
    <div
      className="el-scope"
      role="group"
      aria-label="Şehir, lig ve sezon seçimi"
    >
      <div className="el-scope__seg">
        <span className="el-scope__icon">
          <PinIcon />
        </span>

        <div className="el-scope__field">
          <label htmlFor="el-scope-city">Şehir</label>

          <select
            id="el-scope-city"
            value={cityId}
            onChange={(event) => {
              onCityChange?.(event.target.value);
            }}
          >
            {cityOptions.length === 0 && (
              <option value="">Şehir seçin</option>
            )}

            {cityOptions.map((option) => (
              <option
                key={option.id}
                value={option.id}
              >
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <span
        className="el-scope__sep"
        aria-hidden="true"
      />

      <div className="el-scope__seg">
        <span className="el-scope__icon">
          <TrophyIcon />
        </span>

        <div className="el-scope__field">
          <label htmlFor="el-scope-league">Lig</label>

          <select
            id="el-scope-league"
            value={leagueId}
            onChange={(event) => {
              onLeagueChange?.(event.target.value);
            }}
            disabled={
              !cityReady ||
              leagueOptions.length === 0
            }
          >
            {leagueOptions.length === 0 && (
              <option value="">Lig seçin</option>
            )}

            {leagueOptions.map((option) => (
              <option
                key={option.id}
                value={option.id}
              >
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <span
        className="el-scope__sep"
        aria-hidden="true"
      />

      <div className="el-scope__seg">
        <span className="el-scope__icon">
          <CalendarIcon />
        </span>

        <div className="el-scope__field">
          <label htmlFor="el-scope-season">Sezon</label>

          <select
            id="el-scope-season"
            value={seasonId}
            onChange={(event) => {
              onSeasonChange?.(event.target.value);
            }}
            disabled={
              !cityReady ||
              !leagueId ||
              seasonOptions.length === 0
            }
          >
            {seasonOptions.length === 0 && (
              <option value="">Sezon seçin</option>
            )}

            {seasonOptions.map((option) => (
              <option
                key={option.id}
                value={option.id}
              >
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   ANA BİLEŞEN
========================================================= */

/**
 * Özel lig sayfasında (Junior Cup) sitenin üst menüsü yerine o sayfanın kendi
 * menüsü çizilir; tasarım ve öğeler yönetimden gelir (bkz. src/portal).
 */
export default function SiteHeader(props) {
  const { portal } = usePortal();
  if (portal) return <PortalHeader portal={portal} {...props} />;
  return <ElitligHeader {...props} />;
}

function ElitligHeader({
  cityOptions = [],
  leagueOptions = [],
  seasonOptions = [],

  selectedCity = "",
  selectedLeague = "",
  selectedSeason = "",

  onCityChange,
  onLeagueChange,
  onSeasonChange,

  scopeLabel = "",
  showFilters = true,
  filtersInHero = false,

  isDarkMode = false,
  onToggleTheme,

  toneClasses, // Eski sayfalarla uyumluluk için tutuluyor.
  hero = null,
}) {
  const location = useLocation();
  // Üst menüdeki Giriş Yap / Kayıt Ol düğmeleri oturuma göre değişir.
  const { user } = useUser() || {};

  const [isMobileMenuOpen, setIsMobileMenuOpen] =
    useState(false);
  const [mobileDrawerView, setMobileDrawerView] = useState("menu");

  const [scrolled, setScrolled] = useState(false);

  void toneClasses;

  /* ---------------------------------------------------------
     OPTION NORMALİZASYONU
  --------------------------------------------------------- */

  const normalizedCityOptions = useMemo(
    () => normalizeOptions(cityOptions),
    [cityOptions]
  );

  const normalizedLeagueOptions = useMemo(
    () => normalizeOptions(leagueOptions),
    [leagueOptions]
  );

  const normalizedSeasonOptions = useMemo(
    () => normalizeOptions(seasonOptions),
    [seasonOptions]
  );

  /* ---------------------------------------------------------
     URL KAPSAMI
  --------------------------------------------------------- */

  const routeScope = useMemo(() => {
    const parts = location.pathname
      .split("/")
      .filter(Boolean);

    const cityIndex = parts.indexOf("sehir");
    const leagueIndex = parts.indexOf("lig");
    const seasonIndex = parts.indexOf("sezon");

    return {
      cityId:
        cityIndex >= 0
          ? decodeSeg(parts[cityIndex + 1])
          : "",

      leagueId:
        leagueIndex >= 0
          ? decodeSeg(parts[leagueIndex + 1])
          : "",

      seasonId:
        seasonIndex >= 0
          ? decodeSeg(parts[seasonIndex + 1])
          : "",
    };
  }, [location.pathname]);

  /* ---------------------------------------------------------
     SEÇİLİ DEĞERLER
  --------------------------------------------------------- */

  const selectedCityId = getOptionId(selectedCity);
  const selectedLeagueId = getOptionId(selectedLeague);
  const selectedSeasonId = getOptionId(selectedSeason);

  const defaultCityId =
    normalizedCityOptions[0]?.id || "";

  const defaultLeagueId =
    normalizedLeagueOptions[0]?.id || "";

  const defaultSeasonId =
    normalizedSeasonOptions[0]?.id || "";

  const cityId =
    selectedCityId ||
    routeScope.cityId ||
    defaultCityId ||
    "";

  const leagueId =
    selectedLeagueId ||
    routeScope.leagueId ||
    defaultLeagueId ||
    "";

  const seasonId =
    selectedSeasonId ||
    routeScope.seasonId ||
    defaultSeasonId ||
    "";

  const cityReady = Boolean(cityId);
  const scopedReady = Boolean(
    cityId &&
      leagueId &&
      seasonId
  );

  /* ---------------------------------------------------------
     PATH ÜRETİMİ
  --------------------------------------------------------- */

  const scopedBase = scopedReady
    ? [
        "/sehir",
        encodeSeg(cityId),
        "lig",
        encodeSeg(leagueId),
        "sezon",
        encodeSeg(seasonId),
      ].join("/")
    : "";

  const cityBase = cityReady
    ? `/sehir/${encodeSeg(cityId)}`
    : "";

  const logoTo = scopedReady
    ? `${scopedBase}/anasayfa`
    : "/";

  const buildTo = (link) => {
    if (!link) {
      return null;
    }

    if (link.type === "scoped") {
      if (!scopedReady) {
        return null;
      }

      return `${scopedBase}/${encodeSeg(link.page)}`;
    }

    if (link.type === "city") {
      if (!cityReady) {
        return null;
      }

      return `${cityBase}/${encodeSeg(link.page)}`;
    }

    // Kapsamdan bağımsız bağlantılar: seçim varsa sorgu olarak taşınır.
    if (link.type === "global") {
      const params = new URLSearchParams();
      if (cityId) params.set("city", String(cityId));
      if (leagueId) params.set("league", String(leagueId));
      if (seasonId) params.set("season", String(seasonId));
      const query = params.toString();
      return query ? `/${encodeSeg(link.page)}?${query}` : `/${encodeSeg(link.page)}`;
    }

    return null;
  };

  /* ---------------------------------------------------------
     EFFECTLER
  --------------------------------------------------------- */

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setMobileDrawerView("menu");
  }, [location.pathname]);

  useEffect(() => {
    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      isMobileMenuOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 12);
    };

    handleScroll();

    window.addEventListener(
      "scroll",
      handleScroll,
      {
        passive: true,
      }
    );

    return () => {
      window.removeEventListener(
        "scroll",
        handleScroll
      );
    };
  }, []);

  // Masaüstünde sol ikon rayı görünür; içerik rayın genişliği kadar sağa kayar.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.add("has-el-rail");
    return () => document.body.classList.remove("has-el-rail");
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [isMobileMenuOpen]);

  /* ---------------------------------------------------------
     HERO VE FİLTRELER
  --------------------------------------------------------- */

  const heroVariant =
    hero?.variant || "default";

  const scopePicker = showFilters ? (
    <ScopePicker
      cityOptions={normalizedCityOptions}
      leagueOptions={normalizedLeagueOptions}
      seasonOptions={normalizedSeasonOptions}
      cityId={cityId}
      leagueId={leagueId}
      seasonId={seasonId}
      cityReady={cityReady}
      onCityChange={onCityChange}
      onLeagueChange={onLeagueChange}
      onSeasonChange={onSeasonChange}
    />
  ) : null;

  const mobileChrome = (
    <div className={`el-mobile-chrome ${showFilters ? "has-filters" : ""}`}>
      {showFilters && <div className="el-mobile-filters">{scopePicker}</div>}
      <nav className="el-mobile-dock" aria-label="Mobil hızlı menü">
        {navLinks
          .filter((link) => ["home", "matches", "players", "table"].includes(link.key))
          .sort(
            (a, b) =>
              ["home", "matches", "players", "table"].indexOf(a.key) -
              ["home", "matches", "players", "table"].indexOf(b.key)
          )
          .map((link) => {
            const to = buildTo(link);
            const shortLabels = {
              home: "Anasayfa",
              matches: "Maçlar",
              players: "Oyuncular",
              table: "Puan Tablosu",
            };

            if (!to) {
              return (
                <span key={link.key} className="el-mobile-dock__item is-disabled" aria-disabled="true">
                  <MobileNavIcon type={link.key} />
                  <span>{shortLabels[link.key]}</span>
                </span>
              );
            }

            return (
              <NavLink
                key={link.key}
                to={to}
                className={({ isActive }) =>
                  `el-mobile-dock__item ${isActive ? "is-active" : ""}`
                }
              >
                <MobileNavIcon type={link.key} />
                <span>{shortLabels[link.key]}</span>
              </NavLink>
            );
          })}
        <button
          type="button"
          className="el-mobile-dock__item"
          onClick={() => {
            setMobileDrawerView("menu");
            setIsMobileMenuOpen(true);
          }}
          aria-label="Menüyü aç"
        >
          <MobileNavIcon type="menu" />
          <span>Menü</span>
        </button>
      </nav>
    </div>
  );

  return (
    <header
      className={[
        "el-header",
        `el-header--${heroVariant}`,
        hero ? "has-hero" : "is-slim",
        isDarkMode ? "dark-mode" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* =====================================================
          ÜST MENÜ
      ====================================================== */}

      <div
        className={[
          "el-topbar",
          scrolled ? "is-scrolled" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {/* 1. satır: marka · şehir/lig/sezon seçici · arama ve giriş
            2. satır: ikonlu ana menü */}
        <div className="el-topbar__inner">
          <Link
            to={logoTo}
            className="el-brand"
            aria-label="ElitLig anasayfa"
          >
            <img
              src={logo}
              alt="ElitLig"
            />

            <span className="el-brand__text">
              <span className="el-brand__name">
                ELİTLİG
              </span>

              <span className="el-brand__tag">
                Halı Saha Ligi
              </span>
            </span>
          </Link>

          {scopePicker && !filtersInHero && (
            <div className="el-topbar__scope desktop-only">
              {scopePicker}
            </div>
          )}

          <div className="el-topbar__auth desktop-only">
            {buildTo(navLinks.find((l) => l.key === "players")) && (
              <Link to={buildTo(navLinks.find((l) => l.key === "players"))} className="el-icon-btn" aria-label="Oyuncu ara" title="Oyuncu ara">
                <NavIcon type="search" size={22} />
              </Link>
            )}
            {onToggleTheme && (
              <button type="button" className="el-icon-btn" onClick={onToggleTheme} aria-label={isDarkMode ? "Açık temaya geç" : "Koyu temaya geç"} title={isDarkMode ? "Açık tema" : "Koyu tema"}>
                {isDarkMode ? <SunIcon /> : <MoonIcon />}
              </button>
            )}
            {user ? (
              <Link to={routeAfterLogin(user)} className="el-auth-btn el-auth-btn--primary">
                <NavIcon type="panel" size={18} />
                {isManagementRole(user.role) ? "Yönetim Paneli" : "Panelim"}
              </Link>
            ) : (
              <>
                <Link to="/giris" className="el-auth-btn">
                  Üye Girişi
                </Link>
                <Link to="/kayit-ol" className="el-auth-btn el-auth-btn--primary">
                  Kayıt Ol
                </Link>
              </>
            )}
          </div>
        </div>

        <nav
          className="el-nav desktop-only"
          aria-label="Ana menü"
        >
          <div className="el-nav__inner">
            {navLinks.map((link) => {
              const to = buildTo(link);

              if (!to) {
                return (
                  <span
                    key={link.key}
                    className="el-nav__item is-disabled"
                    title={
                      link.type === "scoped"
                        ? "Önce şehir, lig ve sezon seçin"
                        : "Önce şehir seçin"
                    }
                    aria-disabled="true"
                  >
                    <NavIcon type={link.key} />
                    {link.label}
                  </span>
                );
              }

              return (
                <NavLink
                  key={link.key}
                  to={to}
                  className={({ isActive }) =>
                    [
                      "el-nav__item",
                      isActive ? "is-active" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")
                  }
                >
                  <NavIcon type={link.key} />
                  {link.label}
                </NavLink>
              );
            })}
          </div>
        </nav>
      </div>

      {/* Sol ikon rayı (masaüstü) */}
      <aside className="el-rail desktop-only" aria-label="Hızlı menü">
        {navLinks.map((link) => {
          const to = buildTo(link);
          if (!to) {
            return (
              <span key={link.key} className="el-rail__item is-disabled" aria-disabled="true" title={link.label}>
                <NavIcon type={link.key} size={24} />
              </span>
            );
          }
          return (
            <NavLink
              key={link.key}
              to={to}
              title={link.label}
              aria-label={link.label}
              className={({ isActive }) => `el-rail__item${isActive ? " is-active" : ""}`}
            >
              <NavIcon type={link.key} size={24} />
            </NavLink>
          );
        })}
        <Link to={user ? routeAfterLogin(user) : "/giris"} className="el-rail__item el-rail__item--foot" title={user ? "Panelim" : "Üye girişi"} aria-label={user ? "Panelim" : "Üye girişi"}>
          <NavIcon type="panel" size={24} />
        </Link>
      </aside>

      {/* =====================================================
          HERO
      ====================================================== */}

      {hero && (
        <div className="el-hero">
          <div className="el-hero__inner">
            <div className="el-hero__copy">
              {hero.eyebrow && (
                <p className="el-hero__eyebrow">
                  {hero.eyebrow}
                </p>
              )}

              {hero.title && (
                <h1 className="el-hero__title">
                  {hero.title}
                </h1>
              )}

              {hero.description && (
                <p className="el-hero__desc">
                  {hero.description}
                </p>
              )}

              {hero.extra || null}
            </div>

            {filtersInHero && (hero.filters || scopePicker) && (
              <div className="el-hero__scope">
                {hero.filters || scopePicker}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Portal, detay sayfalarındaki overflow/transform katmanlarının menüyü gizlemesini engeller. */}
      {typeof document !== "undefined" && createPortal(mobileChrome, document.body)}

      {/* =====================================================
          MOBİL MENÜ
      ====================================================== */}

      <div
        className={[
          "el-drawer-overlay",
          isMobileMenuOpen ? "open" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        aria-hidden={!isMobileMenuOpen}
      >
        <button
          type="button"
          className="el-drawer-backdrop"
          onClick={() => {
            setIsMobileMenuOpen(false);
          }}
          aria-label="Menüyü kapat"
          tabIndex={
            isMobileMenuOpen ? 0 : -1
          }
        />

        <div
          id="el-mobile-drawer"
          className="el-drawer"
          role="dialog"
          aria-modal="true"
          aria-label="Mobil menü"
        >
          <div className="el-drawer__head">
            <Link
              to={logoTo}
              className="el-drawer__brand"
              onClick={() => {
                setIsMobileMenuOpen(false);
              }}
            >
              <img
                src={logo}
                alt="ElitLig"
              />

              <span>ELİTLİG</span>
            </Link>

            <button
              type="button"
              className="el-drawer__close"
              onClick={() => {
                setIsMobileMenuOpen(false);
              }}
              aria-label="Menüyü kapat"
            >
              <CloseIcon />
            </button>
          </div>

          {mobileDrawerView === "scope" && (
            <div className="el-drawer__scope-panel">
              <p className="el-drawer__eyebrow">LİG KAPSAMI</p>
              <h2>Şehir, lig ve sezon</h2>
              <p>İçeriği değiştirmek için seçimlerini buradan güncelle.</p>
              {(normalizedCityOptions.length || normalizedLeagueOptions.length || normalizedSeasonOptions.length) ? (
                <ScopePicker
                  cityOptions={normalizedCityOptions}
                  leagueOptions={normalizedLeagueOptions}
                  seasonOptions={normalizedSeasonOptions}
                  cityId={cityId}
                  leagueId={leagueId}
                  seasonId={seasonId}
                  cityReady={cityReady}
                  onCityChange={onCityChange}
                  onLeagueChange={onLeagueChange}
                  onSeasonChange={onSeasonChange}
                />
              ) : (
                <Link to="/" className="el-drawer__scope-fallback" onClick={() => setIsMobileMenuOpen(false)}>Şehir ve lig seçimine git</Link>
              )}
              <button type="button" className="el-drawer__back-to-menu" onClick={() => setMobileDrawerView("menu")}>Menü bağlantılarını göster</button>
            </div>
          )}

          <nav
            className={`el-drawer__nav ${mobileDrawerView === "scope" ? "is-hidden" : ""}`}
            aria-label="Mobil ana menü"
          >
            {navLinks.map((link) => {
              const to = buildTo(link);

              if (!to) {
                return (
                  <span
                    key={link.key}
                    className="el-drawer__item is-disabled"
                    aria-disabled="true"
                  >
                    {link.label}
                  </span>
                );
              }

              return (
                <NavLink
                  key={link.key}
                  to={to}
                  className={({ isActive }) =>
                    [
                      "el-drawer__item",
                      isActive ? "is-active" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")
                  }
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                  }}
                >
                  {link.label}
                </NavLink>
              );
            })}

            <Link
              to="/"
              className="el-drawer__item el-drawer__item--cities"
              onClick={() => {
                setIsMobileMenuOpen(false);
              }}
            >
              <PinIcon />
              <span>Şehir Seçimi</span>
            </Link>

            <div className="el-drawer__auth">
              {user ? (
                <Link
                  to={routeAfterLogin(user)}
                  className="el-auth-btn el-auth-btn--primary"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {isManagementRole(user.role) ? "Yönetim Paneli" : "Panelim"}
                </Link>
              ) : (
                <>
                  <Link to="/giris" className="el-auth-btn" onClick={() => setIsMobileMenuOpen(false)}>
                    Giriş Yap
                  </Link>
                  <Link
                    to="/kayit-ol"
                    className="el-auth-btn el-auth-btn--primary"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Kayıt Ol
                  </Link>
                </>
              )}
            </div>
          </nav>

          <div className="el-drawer__foot" />
        </div>
      </div>
    </header>
  );
}
